import jwtDecode from 'jwt-decode';
import { store } from '../store';
import { setUser, clearUser } from '../features/auth/authSlice';

const STAFF_EXPIRES_KEY = 'staff_session_expires_at';
const LOYALTY_USER_KEY = 'loyalty_user';
const LOYALTY_TOKEN_KEY = 'loyalty_token';

function getSessionStorage() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }
  } catch {
    // ignore
  }
  return null;
}

function getLocalStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // ignore
  }
  return null;
}

function sessionGet(key) {
  const storage = getSessionStorage();
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function sessionSet(key, value) {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    // ignore
  }
}

function sessionRemove(key) {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // ignore
  }
}

function loyaltyGet(key) {
  const local = getLocalStorage();
  if (local) {
    try {
      const value = local.getItem(key);
      if (value) return value;
    } catch {
      // ignore
    }
  }

  const legacy = sessionGet(key);
  if (legacy && local) {
    try {
      local.setItem(key, legacy);
      sessionRemove(key);
    } catch {
      // ignore
    }
  }

  return legacy;
}

function loyaltySet(key, value) {
  const local = getLocalStorage();
  if (local) {
    try {
      local.setItem(key, value);
    } catch {
      // ignore
    }
  }
  sessionRemove(key);
}

function loyaltyRemove(key) {
  const local = getLocalStorage();
  if (local) {
    try {
      local.removeItem(key);
    } catch {
      // ignore
    }
  }
  sessionRemove(key);
}

/**
 * Loyalty bearer: memory for the current tab, localStorage for reloads / mobile QR opens.
 * httpOnly cookie remains a secondary auth path for same-origin setups.
 */
let memoryStaffBearer = null;
let memoryLoyaltyBearer = null;

export function isTestBearerMode() {
  return process.env.NODE_ENV === 'test';
}

export function getStaffExpiresAt() {
  const raw = sessionGet(STAFF_EXPIRES_KEY);
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function setStaffExpiresAt(expiresAtMs) {
  if (expiresAtMs) {
    sessionSet(STAFF_EXPIRES_KEY, String(expiresAtMs));
  } else {
    sessionRemove(STAFF_EXPIRES_KEY);
  }
}

export function setStaffExpiresInSeconds(expiresIn) {
  if (expiresIn) {
    setStaffExpiresAt(Date.now() + expiresIn * 1000);
  }
}

export function isStaffSessionExpired() {
  const expiresAt = getStaffExpiresAt();
  if (!expiresAt) {
    return false;
  }
  return expiresAt <= Date.now();
}

/** Uses sessionStorage expiry in production; falls back to JWT exp in tests. */
export function isTokenExpired(token) {
  const expiresAt = getStaffExpiresAt();
  if (expiresAt) {
    return expiresAt <= Date.now();
  }

  if (!token) {
    return false;
  }

  try {
    const decoded = jwtDecode(token);
    return decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

export const adminSession = {
  getBearerToken() {
    return memoryStaffBearer;
  },

  setBearerToken(token) {
    memoryStaffBearer = token || null;
  },

  setSession(user, { expiresIn, expiresAt } = {}) {
    if (user) {
      store.dispatch(
        setUser({
          user,
          token: memoryStaffBearer,
        })
      );
    }

    if (expiresAt) {
      setStaffExpiresAt(expiresAt * 1000);
    } else if (expiresIn) {
      setStaffExpiresInSeconds(expiresIn);
    }
  },

  clear() {
    memoryStaffBearer = null;
    sessionRemove(STAFF_EXPIRES_KEY);
    store.dispatch(clearUser());
  },

  isAuthenticated() {
    if (isStaffSessionExpired()) {
      return false;
    }
    if (memoryStaffBearer) {
      return true;
    }
    return Boolean(getStaffExpiresAt());
  },
};

export const loyaltySession = {
  getBearerToken() {
    if (memoryLoyaltyBearer) {
      return memoryLoyaltyBearer;
    }
    return loyaltyGet(LOYALTY_TOKEN_KEY);
  },

  setBearerToken(token) {
    memoryLoyaltyBearer = token || null;
    if (token) {
      loyaltySet(LOYALTY_TOKEN_KEY, token);
    } else {
      loyaltyRemove(LOYALTY_TOKEN_KEY);
    }
  },

  getUser() {
    const raw = loyaltyGet(LOYALTY_USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  setSession(user, { bearerToken } = {}) {
    if (bearerToken) {
      this.setBearerToken(bearerToken);
    }
    if (user) {
      loyaltySet(LOYALTY_USER_KEY, JSON.stringify(user));
    }
  },

  clear() {
    memoryLoyaltyBearer = null;
    loyaltyRemove(LOYALTY_USER_KEY);
    loyaltyRemove(LOYALTY_TOKEN_KEY);
  },

  isAuthenticated() {
    return Boolean(this.getBearerToken() || this.getUser());
  },
};
